/**
 * @file cache.ts
 * @description Cache utility with TTL, tag-based invalidation, and memory management
 */

import type { CacheItem, CacheOptions } from '../../types/common';

/**
 * Simple in-memory cache with TTL and tag-based invalidation
 */
export class Cache<T = unknown> {
  private store: Map<string, CacheItem<T>> = new Map();
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutes default
  private maxSize: number = 100; // Maximum cache entries

  constructor(defaultTTL?: number, maxSize?: number) {
    if (defaultTTL) this.defaultTTL = defaultTTL;
    if (maxSize) this.maxSize = maxSize;
  }

  /**
   * Get a value from cache
   * Returns null if expired or not found
   */
  get(key: string): T | null {
    const item = this.store.get(key);
    
    if (!item) return null;
    
    // Check if expired
    if (Date.now() > item.expiry) {
      this.store.delete(key);
      return null;
    }
    
    return item.value;
  }

  /**
   * Set a value in cache with optional TTL and tags
   */
  set(key: string, value: T, options?: CacheOptions): void {
    const ttl = options?.ttl ?? this.defaultTTL;
    const expiry = Date.now() + ttl;
    
    // Enforce max size - remove oldest entries
    if (this.store.size >= this.maxSize) {
      const firstKey = this.store.keys().next().value;
      if (firstKey) this.store.delete(firstKey);
    }
    
    this.store.set(key, {
      key,
      value,
      expiry,
      tags: options?.tags,
    });
  }

  /**
   * Check if a key exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Delete a specific key
   */
  delete(key: string): boolean {
    return this.store.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.store.clear();
  }

  /**
   * Invalidate all entries with a specific tag
   */
  invalidateByTag(tag: string): void {
    for (const [key, item] of this.store.entries()) {
      if (item.tags?.includes(tag)) {
        this.store.delete(key);
      }
    }
  }

  /**
   * Remove expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.store.entries()) {
      if (now > item.expiry) {
        this.store.delete(key);
      }
    }
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.store.size;
  }

  /**
   * Get all keys in cache
   */
  keys(): string[] {
    return Array.from(this.store.keys());
  }
}

/**
 * Request deduplication utility
 * Prevents duplicate in-flight requests
 */
export class RequestDeduplicator {
  private inFlightRequests: Map<string, Promise<unknown>> = new Map();

  /**
   * Execute a request with deduplication
   * If a request with the same key is already in flight, return the existing promise
   */
  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Check if request is already in flight
    const existingRequest = this.inFlightRequests.get(key);
    if (existingRequest) {
      return existingRequest as Promise<T>;
    }

    // Create new request
    const request = requestFn()
      .finally(() => {
        // Clean up after request completes
        this.inFlightRequests.delete(key);
      });

    this.inFlightRequests.set(key, request);
    return request;
  }

  /**
   * Clear all in-flight requests
   */
  clear(): void {
    this.inFlightRequests.clear();
  }

  /**
   * Check if a request is in flight
   */
  isInFlight(key: string): boolean {
    return this.inFlightRequests.has(key);
  }

  /**
   * Get number of in-flight requests
   */
  size(): number {
    return this.inFlightRequests.size;
  }
}

/**
 * Create a cached version of an async function
 */
export function withCache<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  options: {
    keyGenerator: (...args: TArgs) => string;
    cache?: Cache<TReturn>;
    ttl?: number;
    tags?: string[];
  }
): (...args: TArgs) => Promise<TReturn> {
  const cache = options.cache ?? new Cache<TReturn>(options.ttl);
  
  return async (...args: TArgs): Promise<TReturn> => {
    const key = options.keyGenerator(...args);
    
    // Check cache first
    const cached = cache.get(key);
    if (cached !== null) {
      return cached;
    }
    
    // Execute function and cache result
    const result = await fn(...args);
    cache.set(key, result, { ttl: options.ttl, tags: options.tags });
    
    return result;
  };
}

/**
 * Create a debounced function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Create a throttled function
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Export singleton instances for common use
export const globalCache = new Cache();
export const globalDeduplicator = new RequestDeduplicator();
